import { IFormControlProps, getFormControllerProps, FormController } from '@gcl/react-core';
import * as React from 'react';
import { DEFAULT_SELECT_PAGE_SIZE } from './constants';
import { ISelectAsyncPaginateProps, IUseFetchOptionsPropsBase, SelectAsyncPaginate } from './SelectAsyncPaginate';

export interface IFormSelectAsyncPaginateProps<T, UseFetchOptionsProps extends IUseFetchOptionsPropsBase>
  extends Omit<
      ISelectAsyncPaginateProps<T, UseFetchOptionsProps>,
      'onChange' | 'ref' | 'id' | 'name' | 'required' | 'pageSize'
    >,
    IFormControlProps {
  keyFieldName?: string;
  pageSize?: number;

  transform?: {
    input: (value: T[] | T) => any;
    output: (value: any[]) => any;
  };
  shouldTransformSingleSelect?: boolean;
}

export const FormSelectAsyncPaginate: <T, UseFetchOptionsProps extends IUseFetchOptionsPropsBase>(
  props: IFormSelectAsyncPaginateProps<T, UseFetchOptionsProps>,
) => React.ReactElement = ({
  rules = {},
  displayFieldName = 'text',
  keyFieldName = 'key',
  multiple = false,
  pageSize = DEFAULT_SELECT_PAGE_SIZE,
  transform,
  shouldTransformSingleSelect = true,

  ...props
}) => {
  const { controllerProps, controlProps, value } = getFormControllerProps(props);

  const defaultTransform = {
    input: (inputValue: any | any[] | null | undefined) => {
      if (inputValue === undefined || inputValue === null) return undefined;
      return shouldTransformSingleSelect && !Array.isArray(inputValue) && !multiple ? [inputValue] : inputValue;
    },
    output: (outputValue: any | any[] | null | undefined) => {
      if (outputValue === undefined || outputValue === null) return undefined;
      return shouldTransformSingleSelect && Array.isArray(value) && !multiple ? outputValue[0] : outputValue;
    },
  };
  const transformSelect = { ...defaultTransform, ...transform };

  return (
    <FormController
      {...controllerProps}
      rules={{ ...rules }}
      value={value}
      renderInputControl={({ field, id, setValue }) => {
        const { value: hookFormValue, name, onBlur, ref, onChange } = field;
        return (
          <SelectAsyncPaginate
            {...controlProps}
            pageSize={pageSize}
            multiple={multiple}
            displayFieldName={displayFieldName}
            keyFieldName={keyFieldName}
            onChange={(v) => {
              onChange(transformSelect.output(v));
            }}
            onBlur={onBlur}
            value={transformSelect.input(hookFormValue)}
            id={id}
            name={name}
            inputRef={ref}
          />
        );
      }}
    />
  );
};
